<template>
  <div class="min-h-screen flex flex-col bg-background text-text">
    <!-- Header with proper logo alignment and size -->
    <header class="bg-background-light border-b border-background-lighter">
      <div class="container mx-auto px-4 py-3">
        <div class="flex items-center justify-between w-full">
          <!-- Left side: Menu toggle button + Logo -->
          <div class="flex items-center">
            <!-- Sidebar toggle button (visible on both mobile and desktop) -->
            <button
                v-if="!isLoginPage  && !isHomePage"
                @click="toggleSidebar"
                class="p-2 mr-3 rounded-md hover:bg-background-lighter transition-colors"
                aria-label="Toggle sidebar"
                :title="sidebarOpen ? 'Hide sidebar' : 'Show sidebar'"
            >
              <svg class="w-6 h-6 text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path  stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>

            <!-- Logo -->
            <router-link to="/" class="flex items-center">
              <img
                  src="/logo_small.png"
                  alt="Eskrag"
                  class="h-10 w-10 mr-3"
                  @error="handleImageError"
              />
              <span class="text-primary text-2xl font-bold">Eskrag</span>
            </router-link>
          </div>

          <!-- Search and actions -->
          <div class="flex items-center space-x-4">
            <!-- Search bar -->
            <div class="relative hidden md:block">
              <input
                  type="text"
                  placeholder="Search products..."
                  class="w-64 bg-background border border-background-lighter rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  v-model="searchQuery"
                  @keyup.enter="handleSearch"
              >
              <button
                  class="absolute right-3 top-2.5 text-text-secondary"
                  @click="handleSearch"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>



            <NotificationPanel />

            <!-- Cart Button -->
            <button
                v-if="!dataStore.shouldHideCart"
                @click="cartStore.toggleCart"
                class="relative p-2 text-text-secondary hover:text-text hover:bg-background-lighter rounded-full transition-colors"
                aria-label="Shopping Cart">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span
                  v-if="cartStore.count > 0"
                  class="absolute -top-1 -right-1 bg-primary text-white rounded-full text-xs w-5 h-5 flex items-center justify-center"
              >
                {{ cartStore.count }}
              </span>
            </button>

            <!-- User actions -->
            <div class="flex items-center">
              <div v-if="authStore.isLoggedIn" class="flex items-center">
                <!-- User dropdown -->
                <div class="relative user-menu">
                  <button
                      @click="showUserMenu = !showUserMenu"
                      class="flex items-center space-x-2 bg-background-light hover:bg-background-lighter px-3 py-2 rounded-md"
                  >
                    <span class="text-sm hidden md:inline">{{ authStore.currentUser }}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-text-secondary" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                    </svg>
                  </button>

                  <!-- Dropdown menu -->
                  <div
                      v-if="showUserMenu"
                      class="absolute right-0 mt-2 w-56 bg-background border border-background-lighter rounded-md shadow-lg z-50"
                  >
                    <div class="py-1">
                      <div class="px-4 py-2 text-xs text-text-secondary font-medium uppercase tracking-wider">
                        {{ authStore.currentUser }}
                      </div>

                      <router-link
                          to="/panel/profile"
                          class="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-background-lighter text-text-primary transition-colors"
                          @click="showUserMenu = false"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Profile
                      </router-link>

                      <router-link
                          to="/panel/orders"
                          class="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-background-lighter text-text-primary transition-colors"
                          @click="showUserMenu = false"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                        My Orders
                      </router-link>

                      <div v-if="!dataStore.shouldUseDAPricing && !dataStore.shouldHideWallet" class="flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Wallet: {{ authStore.formattedWallet }}
                      </div>

                      <div class="border-t border-background-lighter my-1"></div>

                      <button
                          @click="togglePriceVisibility"
                          :disabled="isUpdatingPreference"
                          class="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm hover:bg-background-lighter text-text-primary transition-colors disabled:opacity-60"
                      >
                        <svg v-if="isUpdatingPreference" class="w-4 h-4 animate-spin text-text-secondary" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <svg v-else-if="isPricesHidden" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                        <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        {{ isPricesHidden ? 'Show Prices' : 'Hide Prices' }}
                      </button>

                      <theme-toggle menu-item />

                      <div class="border-t border-background-lighter my-1"></div>

                      <button @click="logout" class="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm hover:bg-background-lighter text-red-500 transition-colors">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <router-link
                  v-else
                  :to="{ path: '/login', query: { redirect: $route.fullPath } }"
                  class="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md text-sm transition-colors"
              >
                Login
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile search (visible only on mobile) -->
      <div class="md:hidden px-4 pb-3">
        <div class="relative">
          <input
              type="text"
              placeholder="Search products..."
              class="w-full bg-background border border-background-lighter rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              v-model="searchQuery"
              @keyup.enter="handleSearch"
          >
          <button
              class="absolute right-3 top-2.5 text-text-secondary"
              @click="handleSearch"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </header>


    <div class="flex flex-1 relative">
      <!-- Mobile overlay (only on mobile) -->
      <div
          v-if="sidebarOpen && !isLoginPage && isMobile"
          class="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          @click="closeSidebar"
      ></div>

      <!-- Sidebar - works on both desktop and mobile -->
      <aside
          v-if="!isLoginPage  && !isHomePage"
          :class="[
            'bg-background border-r border-background-lighter transition-all duration-300 ease-in-out z-50',
            // Mobile: fixed overlay behavior
            'md:relative',
            // Width changes based on state
            sidebarOpen ? 'w-80 md:w-96' : 'w-0 md:w-0',
            // Position
            'fixed inset-y-0 left-0 md:relative md:inset-auto',
            // Visibility
            sidebarOpen ? 'translate-x-0' : '-translate-x-full md:-translate-x-0',
            // Overflow
            'overflow-hidden'
          ]"
      >
        <!-- Mobile header with close button -->
        <div class="md:hidden flex items-center justify-between p-4 border-b border-background-lighter">
          <h2 class="text-lg font-semibold text-text-primary">Categories</h2>
          <button
              @click="closeSidebar"
              class="p-2 rounded-md hover:bg-background-lighter transition-colors"
          >
            <svg class="w-6 h-6 text-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Sidebar content -->
        <div class="p-4 overflow-y-auto h-full">
          <nav>
            <div v-if="loading" class="flex justify-center py-4">
              <div class="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>

            <template v-else>
              <transition-group
                  name="subcategory-list"
                  tag="div"
                  class="space-y-4"
                  mode="out-in">

                <!-- If a category is selected, show its subcategories -->
                <template v-if="selectedCategory">
                  <div key="back-button" class="mb-4">
                    <button
                        @click="clearSelectedCategory"
                        class="flex items-center text-text-secondary hover:text-primary transition-colors p-2 rounded-lg hover:bg-background-lighter w-full text-left"
                    >
                      <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                      </svg>
                      <span class="truncate">Back to All Categories</span>
                    </button>
                  </div>

                  <router-link
                      v-for="subcategory in categorySubcategories"
                      :key="`category-${subcategory.id}`"
                      :to="`/${selectedCategory.libelle}/${subcategory.libelle}`"
                      class="menu-item mb-4 block"
                      custom
                      v-slot="{ navigate, isActive }"
                      @click="handleSubcategoryClick"
                  >
                    <div @click="navigateToSubcategory(subcategory, navigate)"
                         class="flex items-center cursor-pointer group rounded-lg p-2 transition-all duration-200">
                      <div class="w-16 h-16 mr-3 flex-shrink-0 bg-background-lighter rounded-lg flex items-center justify-center transition-all duration-200 overflow-hidden group-hover:shadow-md">
                        <img
                            :src="getImageUrl(subcategory)"
                            :alt="subcategory.libelle"
                            class="w-full h-full object-contain rounded-lg transition-transform duration-300 group-hover:scale-110"
                            @error="handleImageError"
                        />
                      </div>
                      <div class="text-base font-bold truncate transition-colors duration-200 min-w-0"
                           :class="isActive ? 'text-primary' : 'group-hover:text-primary'">
                        {{ subcategory.libelle || 'Unnamed Subcategory' }}
                      </div>
                    </div>
                  </router-link>
                </template>

                <!-- Otherwise show first subcategories -->
                <template v-else>
                  <router-link
                      v-for="subcategory in limitedSubcategories"
                      :key="`limited-${subcategory.id}`"
                      :to="`/subcategory/${subcategory.id}`"
                      class="menu-item mb-4 block"
                      custom
                      v-slot="{ navigate, isActive }"
                      @click="handleSubcategoryClick"
                  >
                    <div @click="navigateToSubcategory(subcategory, navigate)"
                         class="flex items-center cursor-pointer group rounded-lg p-2 transition-all duration-200"
                         :class="isActive ? 'bg-opacity-10 border-l-4 border-primary' : ''">
                      <div class="w-16 h-16 mr-3 flex-shrink-0 bg-background-lighter rounded-lg flex items-center justify-center transition-all duration-200 overflow-hidden group-hover:shadow-md">
                        <img
                            :src="getImageUrl(subcategory)"
                            :alt="subcategory.libelle"
                            class="w-full h-full object-contain rounded-lg transition-transform duration-300 group-hover:scale-110"
                            @error="handleImageError"
                        />
                      </div>
                      <div class="text-base font-bold truncate transition-colors duration-200 min-w-0"
                           :class="isActive ? 'text-primary' : 'group-hover:text-primary'">
                        {{ subcategory.libelle }}
                      </div>
                    </div>
                  </router-link>
                </template>

              </transition-group>
            </template>
          </nav>
        </div>
      </aside>

      <!-- Main content with dynamic margin -->
      <main :class="[
        'flex-1 overflow-auto transition-all duration-300 ease-in-out',
        !isLoginPage && sidebarOpen ? 'md:ml-0' : 'md:ml-0'
      ]">
        <div class="container mx-auto p-4">
          <!-- Ad banner -->
          <div v-if="dataStore.ads && dataStore.ads.length > 0 && !isLoginPage && !isSubcategoryDetailPage && !isRechargePage" class="my-8">
            <ad-slider :ads="dataStore.ads" />
          </div>
          <br>

          <!-- Categories section - NEW HORIZONTAL SCROLLABLE DESIGN -->
          <div v-if="categories.length > 0 && !isLoginPage && !(isMobile && isCheckoutPage)" class="mb-8 flex justify-center">
            <div class="flex overflow-x-auto pb-3 hide-scrollbar max-w-3xl">
              <!-- All category option -->
              <div
                  class="category-item flex-shrink-0 mx-2 text-center cursor-pointer transition-all duration-200"
                  :class="{'selected-category': !selectedCategory}"
                  @click="navigateToAllProducts"
              >
                <div class="category-icon overflow-hidden mb-2 mx-auto transition-all duration-200"
                     :class="!selectedCategory ? 'scale-110' : ''">
                  <div class="w-full h-full bg-primary flex items-center justify-center text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </div>
                </div>
                <div class="font-medium text-sm transition-colors"
                     :class="{'text-primary font-bold': !selectedCategory}">All</div>
              </div>

              <!-- Recharge shortcut (DZ active users only) -->
              <div
                  v-if="isRechargeAvailable"
                  class="category-item flex-shrink-0 mx-2 text-center cursor-pointer transition-all duration-200"
                  :class="{'selected-category': isRechargePage}"
                  @click="$router.push('/recharge')"
              >
                <div class="category-icon overflow-hidden mb-2 mx-auto transition-all duration-200"
                     :class="isRechargePage ? 'scale-110' : ''">
                  <div class="w-full h-full bg-green-600 flex items-center justify-center text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h2l1 2h13l1-2h2M7 12v6a2 2 0 002 2h6a2 2 0 002-2v-6M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
                    </svg>
                  </div>
                </div>
                <div class="font-medium text-sm transition-colors"
                     :class="{'text-green-600 font-bold': isRechargePage}">Recharge</div>
              </div>


              <div
                  v-for="category in categories"
                  :key="category.id"
                  class="category-item flex-shrink-0 mx-2 text-center cursor-pointer transition-all duration-200"
                  :class="{'selected-category': isSelectedCategory(category)}"
                  @click="navigateToCategory(category)"
              >
                <div class="category-icon overflow-hidden mb-2 mx-auto transition-all duration-200"
                     :class="isSelectedCategory(category) ? 'scale-110' : ''">
                  <img
                      :src="getImageUrl(category)"
                      :alt="category.libelle"
                      class="w-full h-full object-cover transition-transform"
                      @error="handleImageError"
                  />
                </div>
                <div class="font-medium text-sm transition-colors"
                     :class="{'text-primary font-bold': isSelectedCategory(category)}"
                     style="max-width: 80px;">
                  {{ truncate(category.libelle || 'Unnamed', 15) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Error boundary -->
          <div v-if="hasError" class="bg-red-100 dark:bg-red-900 p-4 rounded-lg mb-4">
            <h2 class="font-bold text-red-800 dark:text-red-200">Error Loading Content</h2>
            <p class="text-red-700 dark:text-red-300">
              Sorry, something went wrong while loading this page.
              <button @click="resetError" class="underline font-medium">Try again</button>
            </p>
          </div>

          <slot v-if="!hasError"></slot>
        </div>
      </main>
    </div>

    <div
        v-if="showVideoModal"
        class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
        @click="closeVideoModal"
    >
      <div
          class="bg-background rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          @click.stop
      >
        <!-- Modal Header -->
        <div class="flex items-center justify-between p-4 border-b border-background-lighter">
          <h2 class="text-xl font-bold text-text-primary">How to Use Eskrag - Tutorial</h2>
          <button
              @click="closeVideoModal"
              class="text-text-secondary hover:text-text-primary p-1 rounded transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Video Content -->
        <div class="relative" style="padding-bottom: 56.25%; height: 0;">
          <iframe
              :src="youtubeEmbedUrl"
              class="absolute top-0 left-0 w-full h-full"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
          ></iframe>
        </div>

        <!-- Modal Footer -->
        <div class="p-4 bg-background-light">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <label class="flex items-center cursor-pointer">
                <input
                    type="checkbox"
                    v-model="dontShowAgain"
                    class="rounded border-background-lighter text-primary focus:ring-primary"
                >
                <span class="ml-2 text-sm text-text-secondary">Don't show this again</span>
              </label>
            </div>
            <div class="flex space-x-3">
              <button
                  @click="closeVideoModal"
                  class="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
              >
                Close
              </button>
              <a
                  :href="youtubeDirectUrl"
                  target="_blank"
                  class="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg transition-colors"
              >
                Watch on YouTube
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Floating Help Button -->
    <div
        v-if="!isLoginPage"
        class="fixed bottom-6 right-6 z-40"
    >
      <!-- Main Help Button -->
      <button
          @click="toggleHelpMenu"
          class="bg-primary hover:bg-primary-dark text-white p-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          :class="{ 'animate-bounce': !hasSeenTutorial }"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </button>

      <!-- Help Menu -->
      <div
          v-if="showHelpMenu"
          class="absolute bottom-16 right-0 bg-background border border-background-lighter rounded-xl shadow-xl p-2 min-w-64 animate-scale-up"
      >
        <div class="space-y-1">
          <button
              @click="openVideoModal"
              class="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-background-lighter transition-colors text-left"
          >
            <svg class="w-5 h-5 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"/>
            </svg>
            <div>
              <div class="font-medium text-text-primary">Video Tutorial</div>
              <div class="text-sm text-text-secondary">Learn how to use Eskrag</div>
            </div>
          </button>

          <router-link
              to="/faq"
              class="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-background-lighter transition-colors"
              @click="closeHelpMenu"
          >
            <svg class="w-5 h-5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <div>
              <div class="font-medium text-text-primary">FAQ</div>
              <div class="text-sm text-text-secondary">Common questions</div>
            </div>
          </router-link>

          <router-link
              to="/contact"
              class="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-background-lighter transition-colors"
              @click="closeHelpMenu"
          >
            <svg class="w-5 h-5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <div>
              <div class="font-medium text-text-primary">Contact Support</div>
              <div class="text-sm text-text-secondary">Get help from our team</div>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <Footer />
    <Cart v-if="!dataStore.shouldHideCart" />
  </div>
</template>

<script>
import { ref, onMounted, computed, onErrorCaptured, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ThemeToggle from '../components/ui/ThemeToggle.vue';
import AdSlider from '../components/ui/AdSlider.vue';
import { useDataStore } from '@/stores/dataStore.js'
import { useAuthStore } from '../stores/auth';
import Footer from '../components/Footer.vue';
import { useCartStore } from '../stores/cartStore';
import Cart from '../components/Cart.vue';
import { updateUserPreferences } from '../services/api';
import { destroyEcho } from '../services/echo';
import {slugify} from "@/utils/slugify.js";
import NotificationPanel from "@/components/notifications/NotificationPanel.vue";

export default {
  name: 'MainLayout',
  components: {
    NotificationPanel,
    ThemeToggle,
    AdSlider,
    Footer,
    Cart
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const dataStore = useDataStore();
    const authStore = useAuthStore();
    const cartStore = useCartStore();

    const showVideoHint = ref(false);
    const showVideoModal = ref(false);
    const showHelpMenu = ref(false);
    const dontShowAgain = ref(false);
    const hasSeenTutorial = ref(false);

// Your YouTube video URL
    const youtubeVideoId = 'fLVgzXRpg_0&t=1s'; // Replace with your actual video ID
    const youtubeEmbedUrl = computed(() => `https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1`);
    const youtubeDirectUrl = computed(() => `https://www.youtube.com/watch?v=${youtubeVideoId}`);

// Check if user has seen the tutorial
    const checkTutorialStatus = () => {
      const seenTutorial = localStorage.getItem('eskrag_tutorial_seen');
      const dismissedHint = localStorage.getItem('eskrag_video_hint_dismissed');

      hasSeenTutorial.value = seenTutorial === 'true';

      // Show hint if user hasn't seen tutorial and hasn't dismissed the hint
      if (!hasSeenTutorial.value && dismissedHint !== 'true') {
        // Show hint after 3 seconds for better UX
        setTimeout(() => {
          showVideoHint.value = true;
        }, 3000);
      }
    };

// Open video modal
    const openVideoModal = () => {
      showVideoModal.value = true;
      showHelpMenu.value = false;
      hasSeenTutorial.value = true;
      localStorage.setItem('eskrag_tutorial_seen', 'true');
    };

// Close video modal
    const closeVideoModal = () => {
      showVideoModal.value = false;

      if (dontShowAgain.value) {
        localStorage.setItem('eskrag_video_hint_dismissed', 'true');
        showVideoHint.value = false;
      }

      dontShowAgain.value = false;
    };

// Dismiss video hint
    const dismissVideoHint = () => {
      showVideoHint.value = false;
      localStorage.setItem('eskrag_video_hint_dismissed', 'true');
    };

// Toggle help menu
    const toggleHelpMenu = () => {
      showHelpMenu.value = !showHelpMenu.value;
    };

// Close help menu
    const closeHelpMenu = () => {
      showHelpMenu.value = false;
    };

    // State variables
    const sidebarOpen = ref(true); // Default to open on desktop
    const hasError = ref(false);
    const searchQuery = ref('');
    const showUserMenu = ref(false);
    const isUpdatingPreference = ref(false);
    const selectedCategory = ref(null);

    // Check if mobile
    const isMobile = computed(() => {
      if (typeof window !== 'undefined') {
        return window.innerWidth < 768;
      }
      return false;
    });
    const isCheckoutPage = computed(() => {
      return route.path.includes('/checkout') || route.name === 'checkout';
    });

    // Get user's hide_price preference
    const isPricesHidden = computed(() => {
      if (!authStore.user) return false;
      return authStore.user.hide_price === 1;
    });

    // Image URL base
    const imageBaseUrl = import.meta.env.VITE_IMAGE_URL || 'http://127.0.0.1:8000/images';

    // Computed props from stores
    const loading = computed(() => dataStore.loading);
    const categories = computed(() => {
      return dataStore.categories.filter(c => ![33, 34, 35].includes(parseInt(c.id)));
    });

    // Get first 10 subcategories for sidebar, excluding hidden parent categories
    const limitedSubcategories = computed(() => {
      return dataStore.subcategories
          .filter(s => ![33, 34, 35].includes(parseInt(s.categorie_id)) && ![162, 163].includes(parseInt(s.id)))
          .slice(0, 10);
    });

    // Get subcategories for selected category
    const categorySubcategories = computed(() => {
      if (!selectedCategory.value) return [];
      // If a hidden category was somehow selected, don't show its subcategories
      if ([33, 34, 35].includes(parseInt(selectedCategory.value.id))) return [];

      const filtered = dataStore.subcategories.filter(subcategory =>
          Number(subcategory.categorie_id) === Number(selectedCategory.value.id) &&
          ![162, 163].includes(parseInt(subcategory.id))
      );
      return filtered;
    });

    // Check if current route is subcategory detail page
    const isSubcategoryDetailPage = computed(() => {
      return route.name === 'subcategory-detail' ||
          route.path.includes('/subcategory/');
    });

    const isRechargePage = computed(() => {
      return route.name === 'recharge' || route.path === '/recharge';
    });

    const isRechargeAvailable = computed(() => {
      if (!authStore.isLoggedIn || !authStore.user) return false;
      const country = dataStore.country || authStore.user?.country || '';
      const isActive = authStore.user?.is_active === '1' || authStore.user?.is_active === 1;
      const loweredCountry = country.toString().toLowerCase();
      return (loweredCountry === 'dz' || loweredCountry === '2') && isActive;
    });

    const isLoginPage = computed(() => {
      return route.name === 'login' || route.path.includes('/login/') ||
          route.name === 'register' || route.path.includes('/register/');
    });

    const isHomePage = computed(() => {
      return route.path === '/' ;
    });

    // Sidebar functions - now works for both mobile and desktop
    const toggleSidebar = () => {
      sidebarOpen.value = !sidebarOpen.value;

      // Store preference in localStorage
      localStorage.setItem('sidebarOpen', sidebarOpen.value.toString());
    };

    const closeSidebar = () => {
      sidebarOpen.value = false;
      localStorage.setItem('sidebarOpen', sidebarOpen.value.toString());
    };

    const openSidebar = () => {
      sidebarOpen.value = true;
      localStorage.setItem('sidebarOpen', sidebarOpen.value.toString());
    };

    // Handle subcategory click
    const handleSubcategoryClick = () => {
      // Only close sidebar on mobile when navigating
      if (isMobile.value) {
        closeSidebar();
      }
    };

    // Navigate to category
    const navigateToCategory = (category) => {
      selectedCategory.value = category;
      if (isMobile.value) {
        closeSidebar();
      }else{
        openSidebar();
      }

      if (!isSubcategoryDetailPage.value || isMobile.value) {
        router.push(`/${slugify(category.libelle)}`);
      }
    };

    // Navigate to all products
    const navigateToAllProducts = () => {
      selectedCategory.value = null;
      if (isMobile.value) {
        closeSidebar();
      }
      router.push('/all-products');
    };

    // Clear selected category
    const clearSelectedCategory = () => {
      selectedCategory.value = null;
    };

    // Navigate to subcategory
    const navigateToSubcategory = (subcategory, navigate) => {


      const filtered = dataStore.categories.find(categorie =>
          categorie.id.toString() === subcategory.categorie_id.toString()
      );


      if (!filtered) {
        return;
      }

      // Generate slugified URL
      const categorySlug = slugify(filtered.libelle);
      const subcategorySlug = slugify(subcategory.libelle);
      const newPath = `/${categorySlug}/${subcategorySlug}`;

      // Close sidebar on mobile
      if (isMobile.value) {
        closeSidebar();
      }

      // Check if we're already on this exact subcategory page
      const currentCategorySlug = route.params.categorySlug;
      const currentSubcategorySlug = route.params.subcategorySlug;

      if (isSubcategoryDetailPage.value &&
          currentCategorySlug === categorySlug &&
          currentSubcategorySlug === subcategorySlug) {
        // Already on this page, just scroll to top
        window.scrollTo(0, 0);
        return;
      }

      // Navigate to the new subcategory
      if (isSubcategoryDetailPage.value) {
        // Replace if already on subcategory page
        router.replace(newPath).then(() => {
          window.scrollTo(0, 0);
        });
      } else {
        // Push if coming from different page
        router.push(newPath).then(() => {
          window.scrollTo(0, 0);
        });
      }
    };

    // Check if category is selected
    const isSelectedCategory = (category) => {
      return selectedCategory.value && selectedCategory.value.id === category.id;
    };

    // Toggle price visibility using the API service
    const togglePriceVisibility = async () => {
      if (isUpdatingPreference.value || !authStore.user) return;

      const previous = authStore.user.hide_price;
      authStore.user = { ...authStore.user, hide_price: previous === 1 ? 0 : 1 };
      isUpdatingPreference.value = true;

      try {
        const response = await updateUserPreferences({ hide_price: authStore.user.hide_price });

        if (response && response.user) {
          authStore.user = {
            ...authStore.user,
            hide_price: response.user.hide_price
          };
          localStorage.setItem('user', JSON.stringify(authStore.user));
          dataStore.setUserData(authStore.user);
        }
      } catch (error) {
        authStore.user = { ...authStore.user, hide_price: previous };
        console.error('Failed to update price visibility:', error);
      } finally {
        isUpdatingPreference.value = false;
      }
    };

    // Error handling
    onErrorCaptured((err, instance, info) => {
      console.error('Error caught by layout:', err);
      hasError.value = true;
      return false;
    });

    const resetError = () => {
      hasError.value = false;
      window.location.reload();
    };

    // Handle search functionality
    const handleSearch = () => {
      if (searchQuery.value.trim() === '') return;
      router.push({ name: 'search', query: { q: searchQuery.value } });
      searchQuery.value = '';
    };

    // Logout function
    const logout = () => {
      destroyEcho();
      authStore.logout();
      showUserMenu.value = false;
      router.push('/login');
    };

    // Helper function to truncate text
    const truncate = (text, length) => {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    };

    // Use image_url if available, otherwise construct URL
    const getImageUrl = (item) => {
      if (!item) return '/placeholder-image.png';
      return item.image_url || `${imageBaseUrl}/${item.image}`;
    };

    // Handle image loading errors
    const handleImageError = (event) => {
      event.target.src = '/placeholder-image.png';
    };

    // Fetch home data from API and initialize auth
    onMounted(async () => {
      await dataStore.fetchData();
      authStore.initAuth();

      // Restore sidebar state from localStorage
      const savedSidebarState = localStorage.getItem('sidebarOpen');
      if (savedSidebarState !== null) {
        sidebarOpen.value = savedSidebarState === 'true';
      } else {
        // Default: open on desktop, closed on mobile
        sidebarOpen.value = !isMobile.value;
      }

      // Close menus when clicking outside
      document.addEventListener('click', (e) => {
        if (showUserMenu.value && !e.target.closest('.user-menu')) {
          showUserMenu.value = false;
        }
      });

      // Handle window resize
      window.addEventListener('resize', () => {
        // Update mobile state and adjust sidebar accordingly
        if (window.innerWidth >= 768 && !sidebarOpen.value) {
          // On desktop, restore to open if it was closed on mobile
          const savedState = localStorage.getItem('sidebarOpen');
          if (savedState === null || savedState === 'true') {
            sidebarOpen.value = true;
          }
        }
      });
      checkTutorialStatus();

      // Close help menu when clicking outside
      document.addEventListener('click', (e) => {
        if (showHelpMenu.value && !e.target.closest('.fixed.bottom-6.right-6')) {
          showHelpMenu.value = false;
        }
      });
      // Set selected category based on current route
      if (route.name === 'category' && route.params.id) {
        const categoryId = parseInt(route.params.id);
        const category = dataStore.categories.find(c => c.id === categoryId);
        if (category) {
          selectedCategory.value = category;
        }
      } else if (route.name === 'subcategory-detail' && route.params.id) {
        const subcategoryId = parseInt(route.params.id);
        const subcategory = dataStore.subcategories.find(s => s.id === subcategoryId);
        if (subcategory && subcategory.categorie_id) {
          const category = dataStore.categories.find(c => c.id === subcategory.categorie_id);
          if (category) {
            selectedCategory.value = category;
          }
        }
      }

    });

    // Watch for changes in route
    watch(
        () => route.params,
        (newParams) => {
          if (route.name === 'category' && newParams.id) {
            const categoryId = parseInt(newParams.id);
            const category = dataStore.categories.find(c => c.id === categoryId);
            if (category) {
              selectedCategory.value = category;
            }
          } else if (route.name === 'subcategory-detail' && newParams.id) {
            const subcategoryId = parseInt(newParams.id);
            const subcategory = dataStore.subcategories.find(s => s.id === subcategoryId);
            if (subcategory && subcategory.categorie_id) {
              const category = dataStore.categories.find(c => c.id === subcategory.categorie_id);
              if (category) {
                selectedCategory.value = category;
              }
            }
          }
        },
        { immediate: true }
    );

    return {
      isSelectedCategory,
      sidebarOpen,
      toggleSidebar,
      closeSidebar,
      openSidebar,
      handleSubcategoryClick,
      isMobile,
      loading,
      categories,
      dataStore,
      hasError,
      isSubcategoryDetailPage,
      isRechargePage,
      isRechargeAvailable,
      isLoginPage,
      isHomePage,
      resetError,
      truncate,
      getImageUrl,
      handleImageError,
      searchQuery,
      showUserMenu,
      handleSearch,
      logout,
      authStore,
      cartStore,
      isPricesHidden,
      togglePriceVisibility,
      isUpdatingPreference,
      limitedSubcategories,
      selectedCategory,
      categorySubcategories,
      navigateToCategory,
      navigateToAllProducts,
      clearSelectedCategory,
      navigateToSubcategory,
      showVideoHint,
      showVideoModal,
      showHelpMenu,
      dontShowAgain,
      hasSeenTutorial,
      youtubeEmbedUrl,
      youtubeDirectUrl,
      openVideoModal,
      closeVideoModal,
      dismissVideoHint,
      toggleHelpMenu,
      closeHelpMenu,
      isCheckoutPage,

    };
  }
}
</script>

<style scoped>
/* Sidebar transition and responsive behavior */
.transition-all {
  transition: all 0.3s ease-in-out;
}

/* Ensure text doesn't wrap awkwardly in collapsed sidebar */
.min-w-0 {
  min-width: 0;
}

/* Ensure sidebar content doesn't overflow */
aside {
  min-height: 0;
}

/* Your existing styles remain the same */
header .container {
  max-width: 100%;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  transition: background-color 0.2s;
}

.menu-item:hover {
  background-color: var(--color-background-lighter);
}

.router-link-active.menu-item {
  background-color: var(--color-background-lighter);
  color: var(--color-primary);
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.category-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.category-item {
  padding-top: 6px;
}

.category-item:hover .category-icon {
  transform: translateY(-3px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
}

.category-item:hover img,
.category-item:hover svg {
  transform: scale(1.1);
}

.category-item:hover .font-medium {
  color: var(--color-primary);
}

.category-item.active .category-icon {
  border: 2px solid var(--color-primary);
}

.category-item.active .font-medium {
  color: var(--color-primary);
  font-weight: 600;
}

.subcategory-list-leave-active {
  transition: all 0.5s ease;
}

.subcategory-list-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.subcategory-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.subcategory-list-enter-active {
  transition-delay: calc(0.1s * var(--i));
}

.subcategory-list-move {
  transition: transform 0.5s ease;
}

.selected-category::after {
  content: '';
  display: block;
  width: 40%;
  height: 3px;
  background-color: var(--color-primary);
  border-radius: 2px;
  margin: 4px auto 0;
}
@keyframes slide-down {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.animate-slide-down {
  animation: slide-down 0.5s ease-out;
}

/* Animation for help menu */
@keyframes scale-up {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-scale-up {
  animation: scale-up 0.2s ease-out;
}

/* Bounce animation for help button */
@keyframes bounce {
  0%, 20%, 53%, 80%, 100% {
    animation-timing-function: cubic-bezier(0.215, 0.610, 0.355, 1.000);
    transform: translate3d(0,0,0);
  }
  40%, 43% {
    animation-timing-function: cubic-bezier(0.755, 0.050, 0.855, 0.060);
    transform: translate3d(0, -6px, 0);
  }
  70% {
    animation-timing-function: cubic-bezier(0.755, 0.050, 0.855, 0.060);
    transform: translate3d(0, -3px, 0);
  }
  90% {
    transform: translate3d(0, -1px, 0);
  }
}

.animate-bounce {
  animation: bounce 2s infinite;
}

/* Modal backdrop blur effect */
.backdrop-blur {
  backdrop-filter: blur(4px);
}
</style>
